import { normalizeProductImage } from "../lib/fallback-image";
import { EnergyLabelButtons, type EnergyDoc } from "../lib/energy-pis";
import { useLabel } from "../lib/labels";

// spec_table display 라벨 묶음 — 모듈 헬퍼/중첩 컴포넌트로 전달(단일 경로).
interface SpecLabels {
  yes: string;
  no: string;
  key: string;
  tier2: string;
  general: string;
  specCol: string;
  keySpec: string;
  detailSpecTitle: string;
  noSpecData: string;
  metaLine: string;
  bundleMetaLine: string;
  dimensions: string;
}

function useSpecLabels(): SpecLabels {
  return {
    yes: useLabel("yes", "Yes"),
    no: useLabel("no", "No"),
    key: useLabel("key", "Key"),
    tier2: useLabel("tier2", "Tier 2"),
    general: useLabel("general", "General"),
    specCol: useLabel("specCol", "Spec"),
    keySpec: useLabel("keySpec", "Key spec"),
    detailSpecTitle: useLabel("detailSpecTitle", "{label} Detail Spec"),
    noSpecData: useLabel("noSpecData", "No spec data available for {sku}."),
    metaLine: useLabel("metaLine", "{n} specs · {sku}"),
    bundleMetaLine: useLabel("bundleMetaLine", "{n} items · {sku}"),
    dimensions: useLabel("dimensions", "Dimensions"),
  };
}

interface SpecItem {
  specCode: string;
  label: string;
  category?: string;
  value?: string;
  unit?: string;
  keyTier?: number;
  fact?: { num?: number | null; bool?: boolean | null; ord?: number | null; raw?: string; link?: string };
}

// §4-3 강점 설명 문구 — MCP 도구 시점 LLM 생성(spec.py _attach_strength_callouts).
interface StrengthCallout {
  specCode: string;
  text: string;
}

// Bundle Spec §4-4: 구성품별 Key spec 묶음 (백엔드 spec.py _build_bundle_spec_payload).
interface BundleSpecGroup {
  sku: string;
  name: string;
  specs: SpecItem[];
  totalCount: number;
  // §4-4: 구성품별 AI 강점 설명(spec.py) — Figma "ai desc" 그라데이션 박스.
  strengthCallouts?: StrengthCallout[];
}

// Figma "ico-arrow-down" — 아코디언 chevron(열림 시 CSS 로 뒤집음).
function Chevron() {
  return (
    <svg
      className="ac-spec__bundle-chevron"
      viewBox="0 0 16 16"
      aria-hidden="true"
    >
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface Props {
  data: {
    sku: string;
    locale?: string;
    specs: SpecItem[];
    totalCount: number;
    models?: string[];
    comparisons?: Record<string, Record<string, string>>;
    titleMode?: "key" | "detail";
    focusSpecLabel?: string;
    // §4-3 강점 강조 spec — Compare highlightColumns 와 동일하게 도구 시점 결정(spec.py).
    highlightSpecCodes?: string[];
    // §4-3 강점 설명 문구 — 도구 시점 LLM 생성(spec.py). 핑크 배경 박스로 렌더.
    strengthCallouts?: StrengthCallout[];
    isBundle?: boolean;
    bundleSpecs?: BundleSpecGroup[];
    // §4-6 에너지 등급 PDF 버튼(SKU 제목) + §2-4 에너지 전용 분기(spec.py).
    energyDocs?: EnergyDoc[];
    isEnergyOnly?: boolean;
    dimensionImage?: string;
    dimensionImageMobile?: string;
    dimensionImageAlt?: string;
  };
  sessionToken: string;
}

// §7 Summary(Dimensions) 외형 치수 이미지 — 닷컴 PD0008 `.c-specs-summary` 대응.
function DimensionImage({ data, labels }: { data: Props["data"]; labels: SpecLabels }) {
  const pc = normalizeProductImage(data.dimensionImage);
  const mobile = normalizeProductImage(data.dimensionImageMobile) || pc;
  if (!pc && !mobile) return null;
  return (
    <div className="ac-spec__dimensions">
      <picture>
        {mobile && <source media="(max-width: 768px)" srcSet={mobile} />}
        {pc && <source media="(min-width: 769px)" srcSet={pc} />}
        <img
          className="ac-spec__dimensions-img"
          src={mobile || pc}
          alt={data.dimensionImageAlt || labels.dimensions}
          loading="lazy"
        />
      </picture>
    </div>
  );
}

function formatValue(spec: SpecItem, labels: SpecLabels): string {
  if (spec.value) return spec.value;
  if (spec.fact?.raw) return spec.fact.raw;
  if (spec.fact?.num != null) {
    const num = spec.fact.num;
    return spec.unit ? `${num} ${spec.unit}` : String(num);
  }
  if (spec.fact?.bool != null) return spec.fact.bool ? labels.yes : labels.no;
  return "—";
}

function tierBadge(tier: number | undefined, labels: SpecLabels) {
  if (!tier || tier > 2) return null;
  const label = tier === 1 ? labels.key : labels.tier2;
  const cls = tier === 1
    ? "ac-spec__tier ac-spec__tier--key"
    : "ac-spec__tier ac-spec__tier--2";
  return <span className={cls}>{label}</span>;
}

function groupByCategory(specs: SpecItem[]): Map<string, SpecItem[]> {
  const map = new Map<string, SpecItem[]>();
  for (const s of specs) {
    const cat = s.category || "General";
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(s);
  }
  return map;
}

function specCardTitle(data: Props["data"], labels: SpecLabels): string {
  if (data.titleMode === "detail" && data.focusSpecLabel) {
    return labels.detailSpecTitle.replace("{label}", data.focusSpecLabel);
  }
  return labels.keySpec;
}

// §4 Spec Summary — 시안: 카테고리 그룹 헤더(전체 Spec) + "• 항목 : 값" 불릿 리스트.
// §4-3 강점 spec: 값은 guide 텍스트 그라데이션(.pd-cell.highlight) + 설명은 핑크 배경
// 박스(.interest-col). 둘 다 도구 시점 결정값(highlightSpecCodes·strengthCallouts, spec.py).
function SpecSummaryList({
  specs,
  highlightCodes,
  calloutMap,
  labels,
}: {
  specs: SpecItem[];
  highlightCodes?: Set<string>;
  calloutMap?: Map<string, string>;
  labels: SpecLabels;
}) {
  const grouped = groupByCategory(specs);
  // 전체 Spec은 카테고리 그룹 헤더(GAMING/PICTURE…). 특정 Spec(단일 카테고리)은 헤더 생략.
  const showGroupTitles = grouped.size > 1;
  return (
    <div className="ac-spec__summary-wrap">
      {[...grouped.entries()].map(([category, catSpecs]) => (
        <div key={category} className="ac-spec__group">
          {showGroupTitles && category !== "General" && (
            <h3 className="ac-spec__group-title">{category}</h3>
          )}
          <ul className="ac-spec__list">
            {catSpecs.map((spec) => {
              const callout = calloutMap?.get(spec.specCode);
              const valueCls = highlightCodes?.has(spec.specCode)
                ? "ac-spec__list-value ac-spec__list-value--hl"
                : "ac-spec__list-value";
              return (
                <li
                  key={spec.specCode}
                  className={
                    callout
                      ? "ac-spec__list-item ac-spec__list-item--with-callout"
                      : "ac-spec__list-item"
                  }
                >
                  <div className="ac-spec__list-row">
                    <span className="ac-spec__list-label">{spec.label}</span>
                    <span className="ac-spec__list-sep">:</span>
                    <span className={valueCls}>{formatValue(spec, labels)}</span>
                  </div>
                  {callout && <p className="ac-spec__callout">{callout}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function SpecTableCard({ data }: Props) {
  const { sku, specs } = data;
  const labels = useSpecLabels();
  const models = data.models && data.models.length > 0 ? data.models : [sku];
  const comparisons = data.comparisons || {};
  const isMultiModel = models.length > 1;
  const highlightCodes = new Set(data.highlightSpecCodes || []);
  const calloutMap = new Map(
    (data.strengthCallouts || [])
      .filter((c) => c.specCode && c.text?.trim())
      .map((c) => [c.specCode, c.text.trim()]),
  );
  const specTitle = specCardTitle(data, labels);
  const energyDocs = data.energyDocs || [];

  // Bundle Spec §4-4: 구성품별 아코디언(첫 구성품 default open). 부모 specs 는 비어 있음.
  if (data.isBundle && data.bundleSpecs?.length) {
    return (
      <div className="ac-spec ac-spec--bundle">
        <header className="ac-spec__head">
          <div className="ac-spec__head-row">
            <h2 className="ac-spec__title">{specTitle}</h2>
          </div>
          {/* <p className="ac-spec__meta">
            {labels.bundleMetaLine
              .replace("{n}", String(data.bundleSpecs.length))
              .replace("{sku}", sku)}
          </p> */}
        </header>

        {data.bundleSpecs.map((group, i) => (
          <details
            key={group.sku}
            className="ac-spec__bundle-group"
            open={i === 0}
          >
            <summary className="ac-spec__bundle-summary">
              <span className="ac-spec__bundle-name">
                {i + 1}. {group.name}
              </span>
              <Chevron />
            </summary>
            {group.specs.length ? (
              <SpecSummaryList
                specs={group.specs}
                calloutMap={
                  new Map(
                    (group.strengthCallouts || [])
                      .filter((c) => c.specCode && c.text?.trim())
                      .map((c) => [c.specCode, c.text.trim()]),
                  )
                }
                labels={labels}
              />
            ) : (
              <div className="ac-empty">
                {labels.noSpecData.replace("{sku}", group.sku)}
              </div>
            )}
          </details>
        ))}
      </div>
    );
  }

  if (!specs.length) {
    // §4-6 에너지 등급: SKU 제목 + PDF 텍스트 링크(시안 .btn-text, 등급 이미지 없음).
    const showEnergy = data.isEnergyOnly || energyDocs.length > 0;
    if (showEnergy) {
      return (
        <div className="ac-spec ac-spec--summary ac-spec--energy-only ai-gp1--section">
          <EnergyLabelButtons title={sku} docs={energyDocs} />
        </div>
      );
    }
    // AISASSIT-160: 스펙 데이터가 없을 때 "No spec data available for {sku}" 빈 카드를
    // 띄우면, 어시스턴트 안내 텍스트("…no spec data available…")와 중복·모순돼 이상하게
    // 보인다. 데이터 없으면 카드 자체를 렌더하지 않는다(텍스트만 남김).
    return null;
  }

  if (!isMultiModel) {
    return (
      <div className="ac-spec ac-spec--summary">
        <DimensionImage data={data} labels={labels} />
        <header className="ac-spec__head">
          <div className="ac-spec__head-row">
            <h2 className="ac-spec__title">{specTitle}</h2>
          </div>
          {/* <p className="ac-spec__meta">
            {labels.metaLine
              .replace("{n}", String(data.totalCount ?? specs.length))
              .replace("{sku}", sku)}
          </p> */}
        </header>

        <SpecSummaryList
          specs={specs}
          highlightCodes={highlightCodes}
          calloutMap={calloutMap}
          labels={labels}
        />
        <EnergyLabelButtons title={sku} docs={energyDocs} />
      </div>
    );
  }

  const grouped = groupByCategory(specs);
  return (
    <div className="ac-spec">
      <header className="ac-spec__head">
        <div className="ac-spec__head-row">
          <h2 className="ac-spec__title">{specTitle}</h2>
        </div>
        <p className="ac-spec__meta">
          {labels.metaLine
            .replace("{n}", String(data.totalCount))
            .replace("{sku}", models.join(" vs "))}
        </p>
      </header>

      <div className="ac-spec__table-wrap">
        <div className="ac-spec__scroll">
          <table className="ac-spec__table">
            <thead>
              <tr>
                <th className="ac-spec__th-spec">{labels.specCol}</th>
                {models.map((m) => (
                  <th key={m} className="ac-spec__th-model">
                    <span className="ac-spec__th-model-name">{m}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...grouped.entries()].map(([category, catSpecs]) => (
                <CategoryGroup
                  key={category}
                  category={category}
                  specs={catSpecs}
                  models={models}
                  comparisons={comparisons}
                  labels={labels}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CategoryGroup({
  category,
  specs,
  models,
  comparisons,
  labels,
}: {
  category: string;
  specs: SpecItem[];
  models: string[];
  comparisons: Record<string, Record<string, string>>;
  labels: SpecLabels;
}) {
  return (
    <>
      <tr className="ac-spec__cat-row">
        <td colSpan={models.length + 1}>
          {category === "General" ? labels.general : category}
        </td>
      </tr>
      {specs.map((spec, i) => (
        <tr
          key={spec.specCode}
          className={i % 2 === 0 ? "ac-spec__row" : "ac-spec__row ac-spec__row--alt"}
        >
          <td className="ac-spec__cell-name">
            <div className="ac-spec__cell-name-inner">
              <span className="ac-spec__cell-label">{spec.label}</span>
              {tierBadge(spec.keyTier, labels)}
            </div>
          </td>
          {models.map((m) => {
            const val =
              comparisons[m]?.[spec.specCode] ??
              (m === models[0] ? formatValue(spec, labels) : "—");
            return (
              <td key={m} className="ac-spec__cell-val">{val}</td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
